import { buildUrlWithQueryParams } from '../utils/Helper';
import {
  downloadBlob,
  getFileExtensionFromContentType,
  REPORT_TYPE_NAMES,
  generateBackupFilename,
  generateVaultProcessedFilename,
  downloadBlobWithMessage,
  generateReportFilename,
} from '../utils/apiUtils';
import { axiosInstance } from './Infra/AxiosInstance';
import { fetchEventSource } from '@microsoft/fetch-event-source';

// SSE Connection Types
export interface SSEConnectionOptions<T> {
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onClose?: () => void;
}

// Generic SSE connection factory - returns AbortController for connection management
const createSSEConnection = <T>(
  endpoint: string,
  options: SSEConnectionOptions<T>
): AbortController => {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    console.error('No auth token available for SSE connection');
    throw new Error('No auth token available for SSE connection');
  }

  const abortController = new AbortController();
  const baseUrl = import.meta.env.VITE_SERVER_BASE_ADDRESS || '';

  fetchEventSource(`${baseUrl}${endpoint}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal: abortController.signal,

    onmessage(event) {
      try {
        // Skip empty or invalid events to prevent JSON parsing errors
        if (!event.data || event.data.trim() === '') {
          return;
        }
        const data: T = JSON.parse(event.data);
        options.onMessage(data);
      } catch (error) {
        console.error('Failed to parse SSE event:', error, 'Data:', event.data);
        // Continue processing - don't break the stream on malformed events
      }
    },

    onerror(err) {
      console.error('SSE connection error:', err);
      options.onError?.(err);
      throw err; // Prevent auto-retry
    },

    onclose() {
      options.onClose?.();
    },
  });

  return abortController;
};
import {
  // Holdings types
  HoldingsListResponse,
  HoldingsUpdateRequest,
  HoldingsUpdateResponse,
  // OTC types
  OTCListResponse,
  OTCSyncResponse,
  OTCComparisonResponse,
  OTCCheckDifferencesResponse,
  OTCUpdateRequest,
  // Portfolio types
  PortfolioListResponse,
  PortfolioUpdateRequest,
  SyncResponse,
  // Vault types
  VaultProcessResponse,
  BankType,
  // Views types
  AllViewsResponse,
  ViewsPageResponse,
  ViewsBulkSaveResponse,
  ViewActionResponse,
  ViewItem,
  PageType,
  // Groups types
  AllGroupsResponse,
  GroupsPageResponse,
  GroupsBulkSaveResponse,
  GroupItem,
  GroupPageType,
  // DataFeed types
  DataFeedUploadResponse,
  DataFeedFileResponse,
  DataFeedHeadersResponse,
  CellUpdateRequest,
  DataFeedRowData,
  CellUpdateResponse,
  DeleteRowRequest,
  DeleteRowResponse,
  // Configuration types
  DanelConfiguration,
  DanelMapping,
  SingleRowConfiguration,
  FieldTransformation,
  ExtraFieldConfiguration,
  ApplyTemplateRulesResponse,
  // Authentication types
  LoginRequest,
  LoginResponse,
  UserResponse,
  OrganizationResponse,
  // Service types
  ServiceResponse,
  // Trades types
  TradeUploadResponse,
  // Reports types
  PortfolioInfo,
  AllFamiliesReportResponse,
  ReportType,
  FailedPortfolioInfo,
  // Wizard types
  FileColumnsResponse,
  ColumnMappingRequest,
  SessionDataResponse,
  SessionCreateResponse,
  FinalizeResponse,
  WizardSessionsListResponse,
  InstrumentMappingRequest,
  UploadStatusResponse,
  AccountsResponse,
  CustodiansResponse,
  AccountMappingRequest,
  UploadedFileInfo,
  AccountsMappingStatusResponse,
  InstrumentsMatchResponse,
  IllioSearchResponse,
  IllioOTCDatabaseListResponse,
  IllioOTCDatabaseWithLinksListResponse,
  IllioOTCDatabaseUpdateRequest,
  IllioOTCDatabaseUpdateResponse,
  OTCMatchResponse,
  OTCInstrumentsResponse,
  OTCMappingRequest,
  WizardStepStatusResponse,
  WizardStep,
  VaultDetectionResponse,
  VaultParseResponse,
  ProcessedFileResponse,
  UploadProcessedRowsResponse,
  // Positions Monitoring types
  PositionComparisonResponse,
  // Price Fetching types
  PriceFetchItem,
  FetchPricesResponse,
  // Flows & Automations - Templates types
  TemplateResponse,
  TemplateListResponse,
  FlowsActionResponse,
  FlowsDeleteResponse,
  // Flows & Automations - PDF Editor types
  PlaceholderRect,
  PlaceholderStyle,
  PlaceholderData,
  PDFInfoResponse,
  DBColumnsResponse,
  FriendlyColumnInfo,
  DBColumnsFriendlyResponse,
  ColumnValueInfo,
  ColumnValuesResponse,
  FontDetectionResponse,
  // Flows & Automations - Clients & Documents types
  TemplateClientResponse,
  ClientListResponseTemplates,
  CollectionFieldInfo,
  CollectionsListResponse,
  ClientRelatedDataResponse,
  GeneratedDocumentResponse,
  GeneratedDocumentListResponse,
  SaveGeneratedDocumentResponse,
  SaveGeneratedDocumentRequest,
  // Flows & Automations - Flows types
  FlowConditionResponse,
  FlowEmailActionResponse,
  FlowTemplateActionResponse,
  FlowResponse,
  FlowListResponse,
  FlowRunResponse,
  FlowRunListResponse,
  RecordFlowRunRequest,
  // Flows & Automations - Transactions types
  TransactionResponse,
  TransactionListResponse,
  SendTransactionEmailRequest,
  SendTransactionEmailResponse,
  // Flows & Automations - Data Sources types
  ClientOption,
  CustodianOption,
  InstrumentOption,
  FlowsClientListResponse,
  CustodianListResponse,
  InstrumentListResponse,
  // Flows & Automations - Flow Matching types
  MatchedFlowEmailAction,
  MatchedFlowTemplateAction,
  MatchedFlow,
  FlowMatchTransactionResponse,
  // Flows & Automations - Flow Fields and Email Variables
  FlowFieldsResponse,
  EmailVariablesResponse,
} from '../types/generatedTypes';

// Re-export types that are used by other components
export type { MatchedFlow } from '../types/generatedTypes';

import { ConfigurationTemplate } from '../components/DataFeed/Configuration/types';
import { RowType } from '../types/rowType';

const getAllFamiliesReport = async (params?: {
  fromDate?: string;
  toDate?: string;
  portfolios?: PortfolioInfo[];
}): Promise<AllFamiliesReportResponse> => {
  const { fromDate, toDate, portfolios } = params || {};

  // Build URL with date query params only
  const url = buildUrlWithQueryParams(`all-families`, { fromDate, toDate });

  // Send portfolios data in the request body
  const response = await axiosInstance.post(
    url,
    {
      portfolios: portfolios || [],
    },
    {
      responseType: 'blob',
    }
  );

  downloadBlob(response.data, 'All Families Report.xlsx');

  // Return a successful response for typing purposes
  return {
    message: 'All families report generated successfully',
    filename: 'All Families Report.xlsx',
    report_type: 'all_families',
    from_date: fromDate || null,
    to_date: toDate || null,
    portfolios_count: portfolios?.length || null,
    content_type:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    portfolios_included: portfolios || [],
  };
};

const generateReport = async (params: {
  reportType: ReportType;
  portfolios: PortfolioInfo[];
  fromDate?: string;
  toDate?: string;
  isInception: boolean;
  filenameTemplate?: string;
}): Promise<AllFamiliesReportResponse> => {
  const {
    reportType,
    portfolios,
    fromDate,
    toDate,
    isInception,
    filenameTemplate,
  } = params;

  // Send ALL data in request body to match backend GenerateReportRequest model
  const response = await axiosInstance.post(
    'generate-report', // Correct endpoint path (not reports/generate/${reportType})
    {
      portfolios,
      reportType, // In body, not URL
      fromDate, // In body, not query param
      toDate, // In body, not query param
      isInception, // In body with correct backend field name
      filenameTemplate, // In body for custom filename
    },
    {
      responseType: 'blob',
    }
  );

  // Helper to extract headers (works with both AxiosHeaders and plain objects)
  const getHeader = (name: string): string | null => {
    const headers = response.headers;

    // Try .get() method first (AxiosHeaders)
    if (typeof headers.get === 'function') {
      const val = headers.get(name);
      if (val) return String(val);
    }

    // Try bracket notation (plain objects)
    const bracketVal = headers[name];
    if (bracketVal) return String(bracketVal);

    // Try lowercase (axios lowercases header names in some cases)
    const lowerVal = headers[name.toLowerCase()];
    if (lowerVal) return String(lowerVal);

    return null;
  };

  // Get content type and file extension
  const contentType = getHeader('content-type') || '';
  const fileExtension = getFileExtensionFromContentType(contentType);

  // Generate filename using utility
  const filename = generateReportFilename({
    filenameTemplate,
    portfolios,
    reportType,
    fromDate,
    toDate,
    fileExtension,
  });

  downloadBlob(response.data, filename);

  // Report type name for message
  const reportTypeName = REPORT_TYPE_NAMES[reportType] || reportType;

  // Extract metadata from custom headers

  const successCount = parseInt(getHeader('x-success-count') || '0');
  const failedCount = parseInt(getHeader('x-failed-count') || '0');
  const totalCount = parseInt(getHeader('x-total-count') || '0');

  const failedPortfoliosHeader = getHeader('x-failed-portfolios');
  const failedPortfolios: FailedPortfolioInfo[] = failedPortfoliosHeader
    ? JSON.parse(failedPortfoliosHeader)
    : [];

  return {
    message: `${reportTypeName} report generated successfully`,
    filename,
    report_type: reportType,
    from_date: fromDate || null,
    to_date: toDate || null,
    portfolios_count: portfolios?.length || null,
    content_type: contentType,
    portfolios_included: portfolios || [],
    metadata: {
      successCount,
      failedCount,
      totalCount,
      failedPortfolios: failedPortfolios.map((fp: FailedPortfolioInfo) => ({
        name: fp.name,
        error: fp.error,
      })),
    },
  };
};

const uploadDanelExcel = async (
  file: File
): Promise<DataFeedUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post<DataFeedUploadResponse>(
    `upload_danel_file`,
    formData
  );
  return response.data;
};
const getDanelExcelHeaders = async (): Promise<string[]> => {
  const response = await axiosInstance.get<DataFeedHeadersResponse>(
    `get_last_danel_file_headers`
  );
  // Handle nested response structure from backend
  if (response.data && response.data.headers) {
    return response.data.headers;
  }
  return [];
};

const getOTCAssets = async (
  includeInactive: boolean = false
): Promise<OTCListResponse> => {
  const response = await axiosInstance.get<OTCListResponse>(
    `display_all_otc?include_inactive=${includeInactive}`
  );
  return response.data;
};

const updateOTC = async (
  updatedRow: OTCUpdateRequest,
  otcId: string
): Promise<void> => {
  await axiosInstance.post(`update_single_otc?otc_id=${otcId}`, updatedRow);
};

const getPortfolioData = async (): Promise<PortfolioListResponse> => {
  const response = await axiosInstance.get<PortfolioListResponse>(
    `display_all_portfolio_data`
  );
  return response.data;
};

const updateCustodianData = async (
  portfolioId: string,
  updateData: PortfolioUpdateRequest
): Promise<void> => {
  await axiosInstance.put(`update_custodian_data/${portfolioId}`, updateData);
};

const uploadOTCToIllio = async (
  changedRows: OTCUpdateRequest[]
): Promise<void> => {
  // ! debug
  await axiosInstance.post(`upload_otc_to_illio`, changedRows);
};

const syncOTCFromIllio = async (): Promise<OTCSyncResponse> => {
  const response =
    await axiosInstance.post<OTCSyncResponse>(`otc/sync-from-illio`);
  return response.data;
};

const getUpdatedOTCComparedToIllio = async (
  otcId: string
): Promise<OTCComparisonResponse> => {
  const response = await axiosInstance.get<OTCComparisonResponse>(
    `get_updated_otc?otc_id=${otcId}`
  );
  return response.data;
};

const checkAllOTCDifferences =
  async (): Promise<OTCCheckDifferencesResponse> => {
    const response = await axiosInstance.post<OTCCheckDifferencesResponse>(
      `otc/check-all-differences`
    );
    return response.data;
  };

const checkOTCDifferencesRetry = async (otcIds: string[]): Promise<OTCCheckDifferencesResponse> => {
  const response = await axiosInstance.post<OTCCheckDifferencesResponse>(
    `otc/check-differences-retry`,
    { otc_ids: otcIds }
  );
  return response.data;
};

const deleteOTCColumn = async (columnToDelete: string): Promise<void> => {
  await axiosInstance.post(`delete_column?column_name=${columnToDelete}`);
};

// Download backup JSON
const downloadOTCBackup = async (): Promise<void> => {
  const response = await axiosInstance.post(
    `otc/backup`,
    {},
    {
      responseType: 'blob',
    }
  );

  const filename = generateBackupFilename('OTC');
  downloadBlob(response.data, filename);
};

// Restore OTC from backup JSON file
// Note: Backend now accepts only JSON files. The endpoint will reject non-JSON files.
const restoreOTCFromBackup = async (file: File): Promise<SyncResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axiosInstance.post<SyncResponse>(
    `otc/restore`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

const getDanelData = async (): Promise<DataFeedFileResponse> => {
  const response = await axiosInstance.get<DataFeedFileResponse>(
    `get_last_danel_file_data`
  );

  return response.data;
};

// Upload Danel rows to Illio
const uploadSingleDanelToIllio = async (
  rows: DataFeedRowData,
  targetPortfolioId: string,
  appliedTemplateId: string | null
): Promise<TradeUploadResponse> => {
  const response = await axiosInstance.post(`upload_single_trade`, {
    trade_data: rows,
    target_portfolio_id: targetPortfolioId,
    applied_template_id: appliedTemplateId,
  });

  const data = response.data;
  if (data.error_type || data.error_code) {
    // This is actually an error response - throw to trigger error handling
    throw new Error(data.message || data.api_error || 'Trade upload failed');
  }

  return response.data;
};

// Upload processed rows to Illio
const uploadProcessedRows = async (
  processedFileId: string,
  rowIndices: number[]
): Promise<UploadProcessedRowsResponse> => {
  const response = await axiosInstance.post<UploadProcessedRowsResponse>(
    `upload_processed_rows`,
    {
      processed_file_id: processedFileId,
      row_indices: rowIndices,
    }
  );

  return response.data;
};

const saveDanelMappings = async (mappings: DanelMapping[]): Promise<void> => {
  // Send the transformed mappings to the server
  await axiosInstance.post(`upload_new_config`, mappings);
};

const getDanelConfiguration = async (): Promise<DanelConfiguration> => {
  const response =
    await axiosInstance.get<DanelConfiguration>(`get_last_config`);
  return response.data;
};

// Reset table configuration
const resetTableConfiguration = async (tableName: string): Promise<void> => {
  await axiosInstance.delete(
    `reset_table_config?table_name=${encodeURIComponent(tableName)}`
  );
};

// Add single row configuration
const addSingleRowConfiguration = async (config: {
  table_name: string;
  field_name: string;
  column: string;
  transformation?: FieldTransformation;
  extra_config?: ExtraFieldConfiguration;
}): Promise<void> => {
  await axiosInstance.post(`add_single_row_config`, config);
};

// Get single row configuration
const getSingleRowConfiguration = async (
  tableName: string,
  fieldName: string
): Promise<SingleRowConfiguration> => {
  const response = await axiosInstance.get<SingleRowConfiguration>(
    `get_single_row_config?table_name=${encodeURIComponent(tableName)}&field_name=${encodeURIComponent(fieldName)}`
  );
  return response.data;
};

// Delete single row configuration
const deleteSingleRowConfiguration = async (
  tableName: string,
  fieldName: string
): Promise<void> => {
  await axiosInstance.delete(
    `delete_single_row_config?table_name=${encodeURIComponent(tableName)}&field_name=${encodeURIComponent(fieldName)}`
  );
};

const updateDanelFileCell = async (
  rowIndex: number,
  columnName: string,
  newValue: string | number | boolean | null
): Promise<CellUpdateResponse> => {
  const requestData: CellUpdateRequest = {
    row_index: rowIndex,
    column_name: columnName,
    new_value: newValue,
  };

  const response = await axiosInstance.post<CellUpdateResponse>(
    `update_danel_file_cell`,
    requestData
  );
  return response.data;
};

const deleteDanelRow = async (
  deleteRequest: DeleteRowRequest
): Promise<DeleteRowResponse> => {
  const response = await axiosInstance.delete<DeleteRowResponse>(
    `delete_danel_row`,
    {
      data: deleteRequest,
    }
  );
  return response.data;
};

// Holdings API functions
const getHoldings = async (): Promise<HoldingsListResponse> => {
  const response =
    await axiosInstance.get<HoldingsListResponse>(`display_all_holdings`);
  return response.data;
};

const getOTCDatabase = async (): Promise<IllioOTCDatabaseListResponse> => {
  const response = await axiosInstance.get<IllioOTCDatabaseListResponse>(
    'display_all_otc_database'
  );
  return response.data;
};

const getOTCDatabaseWithLinks =
  async (): Promise<IllioOTCDatabaseWithLinksListResponse> => {
    const response =
      await axiosInstance.get<IllioOTCDatabaseWithLinksListResponse>(
        'otc_database'
      );
    return response.data;
  };

const syncOTCDatabaseFromIllio = async (): Promise<OTCSyncResponse> => {
  const response = await axiosInstance.post<OTCSyncResponse>(
    'otc_database/sync-from-illio'
  );
  return response.data;
};

const updateOTCDatabaseRecord = async (
  otcId: string,
  updatedRow: IllioOTCDatabaseUpdateRequest
): Promise<IllioOTCDatabaseUpdateResponse> => {
  const response = await axiosInstance.post<IllioOTCDatabaseUpdateResponse>(
    `update_single_otc_database?otc_id=${otcId}`,
    updatedRow
  );
  return response.data;
};

const updateHolding = async (
  holdingId: string,
  updatedRow: HoldingsUpdateRequest
): Promise<HoldingsUpdateResponse> => {
  const response = await axiosInstance.post<HoldingsUpdateResponse>(
    `update_single_holding?holding_id=${holdingId}`,
    updatedRow
  );
  return response.data;
};

const syncPortfolioCustodianFromIllio = async (): Promise<SyncResponse> => {
  const response = await axiosInstance.post<SyncResponse>(
    `sync_portfolio_custodian_from_illio`
  );
  return response.data;
};

// Views API - New unified structure with proper typing
const getAllViews = async (): Promise<AllViewsResponse['views']> => {
  const response = await axiosInstance.get<AllViewsResponse>(`views`);
  return response.data.views;
};

const getViewsForPage = async (pageType: PageType): Promise<ViewItem[]> => {
  const response = await axiosInstance.get<ViewsPageResponse>(
    `views/${pageType}`
  );
  return response.data.views || [];
};

const saveViewsForPage = async (
  pageType: PageType,
  views: ViewItem[]
): Promise<ViewsBulkSaveResponse> => {
  const response = await axiosInstance.post<ViewsBulkSaveResponse>(
    `views/${pageType}`,
    views
  );
  return response.data;
};

const createView = async (
  pageType: PageType,
  view: Omit<ViewItem, 'id'>
): Promise<ViewActionResponse> => {
  const response = await axiosInstance.post<ViewActionResponse>(
    `views/${pageType}/create`,
    view
  );
  return response.data;
};

const updateView = async (
  pageType: PageType,
  viewId: string,
  view: Omit<ViewItem, 'id'>
): Promise<ViewActionResponse> => {
  const response = await axiosInstance.put<ViewActionResponse>(
    `views/${pageType}/${viewId}`,
    view
  );
  return response.data;
};

const deleteView = async (
  pageType: PageType,
  viewId: string
): Promise<ViewActionResponse> => {
  const response = await axiosInstance.delete<ViewActionResponse>(
    `views/${pageType}/${viewId}`
  );
  return response.data;
};

// Groups API - Following same pattern as Views
const getAllGroups = async (): Promise<AllGroupsResponse> => {
  const response = await axiosInstance.get<AllGroupsResponse>(`groups`);
  return response.data;
};

const getGroupsForPage = async (
  pageType: GroupPageType
): Promise<GroupsPageResponse> => {
  const response = await axiosInstance.get<GroupsPageResponse>(
    `groups/${pageType}`
  );
  return response.data;
};

const saveGroupsForPage = async (
  pageType: GroupPageType,
  groups: GroupItem[]
): Promise<GroupsBulkSaveResponse> => {
  const response = await axiosInstance.post<GroupsBulkSaveResponse>(
    `groups/${pageType}`,
    groups
  );
  return response.data;
};

// Authentication API functions
const login = async (loginRequest: LoginRequest): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>(
    `auth/login`,
    loginRequest
  );
  return response.data;
};

const logout = async (): Promise<ServiceResponse> => {
  const response = await axiosInstance.post<ServiceResponse>(`auth/logout`);
  return response.data;
};

const getCurrentUser = async (): Promise<{
  user: UserResponse;
  organization: OrganizationResponse;
}> => {
  const response = await axiosInstance.get<{
    user: UserResponse;
    organization: OrganizationResponse;
  }>(`auth/me`);
  return response.data;
};

const detectVaultTypes = async (
  files: File[]
): Promise<VaultDetectionResponse> => {
  const formData = new FormData();

  files.forEach(file => {
    formData.append('files', file);
  });

  const response = await axiosInstance.post<VaultDetectionResponse>(
    `vault/detect_vault_types`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

const parseVaultFiles = async (
  files: { file: File; bank: BankType; name: string }[]
): Promise<VaultParseResponse> => {
  const formData = new FormData();
  const bankMappings: Record<string, BankType> = {};

  files.forEach(fileInfo => {
    formData.append('files', fileInfo.file);
    bankMappings[fileInfo.name] = fileInfo.bank;
  });

  formData.append('bank_mappings', JSON.stringify(bankMappings));

  const response = await axiosInstance.post<VaultParseResponse>(
    `vault/parse_vault_files`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

const saveParsedVaults = async (
  parsedData: {
    success: boolean;
    filename: string;
    bank_type: string;
    row_count?: number;
    parsed_data: any;
    error?: string | null;
  }[],
  returnExcel: boolean = false,
  rowsType?: RowType
): Promise<VaultProcessResponse | { message: string }> => {
  const payloadToSend = {
    results: parsedData,
    return_excel: returnExcel,
    rows_type: rowsType,
  };

  if (returnExcel) {
    // If requesting Excel download, use blob response type
    const response = await axiosInstance.post(
      `vault/save_parsed_vaults`,
      payloadToSend,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    // Extract file_id and positions_file_id from headers (backend sends them for wizard progression)
    const fileId = response.headers['x-file-id'];
    const positionsFileId = response.headers['x-positions-file-id'];

    // Download the Excel/ZIP file
    const filename = generateVaultProcessedFilename();
    downloadBlobWithMessage(response.data, filename);

    // Return with file_id and positions_file_id so wizard can proceed
    return { message: 'File downloaded successfully', file_id: fileId, positions_file_id: positionsFileId };
  } else {
    // If just saving to database, use JSON response
    const response = await axiosInstance.post<VaultProcessResponse>(
      `vault/save_parsed_vaults`,
      payloadToSend,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  }
};

const processVaultFiles = async (
  files: { file: File; bank: BankType; name: string }[],
  returnExcel: boolean = false
): Promise<VaultProcessResponse | { message: string }> => {
  const formData = new FormData();
  const bankMappings: Record<string, BankType> = {};

  files.forEach(fileInfo => {
    formData.append('files', fileInfo.file);
    bankMappings[fileInfo.name] = fileInfo.bank;
  });

  formData.append('bank_mappings', JSON.stringify(bankMappings));
  formData.append('return_excel', returnExcel.toString());

  if (returnExcel) {
    // If requesting Excel download, use blob response type
    const response = await axiosInstance.post(
      `vault/process_vault_files`,
      formData,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Download the Excel/ZIP file
    const filename = generateVaultProcessedFilename();
    return downloadBlobWithMessage(response.data, filename);
  } else {
    // If just saving to database, use JSON response
    const response = await axiosInstance.post(
      `vault/process_vault_files`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }
};

// Configuration Template API functions
const saveConfigurationTemplate = async (
  template: ConfigurationTemplate
): Promise<void> => {
  await axiosInstance.post(`datafeed/templates/save`, template);
};

const getConfigurationTemplate = async (
  templateId: string
): Promise<ConfigurationTemplate> => {
  const response = await axiosInstance.get(`datafeed/templates/${templateId}`);
  return response.data.template_data;
};

const listConfigurationTemplates = async (): Promise<
  ConfigurationTemplate[]
> => {
  const response = await axiosInstance.get(`datafeed/templates`);
  return response.data.templates;
};

const deleteConfigurationTemplate = async (
  templateId: string
): Promise<void> => {
  await axiosInstance.delete(`datafeed/templates/${templateId}`);
};

const renameConfigurationTemplate = async (
  templateId: string,
  newName: string
): Promise<void> => {
  const template = await getConfigurationTemplate(templateId);
  template.name = newName;
  await saveConfigurationTemplate(template);
};

const applyTemplateRules = async (
  templateId?: string | null,
  sessionId?: string | null,
  preserveEditedValues?: boolean
): Promise<ApplyTemplateRulesResponse> => {
  const body: Record<string, unknown> = { session_id: sessionId };
  if (templateId) body.template_id = templateId;
  if (preserveEditedValues) body.preserve_edited_values = preserveEditedValues;

  const response = await axiosInstance.post<ApplyTemplateRulesResponse>(
    `datafeed/apply-rules`,
    body
  );
  return response.data;
};

const getProcessedFileData = async (
  fileId: string
): Promise<ProcessedFileResponse> => {
  const response = await axiosInstance.get<ProcessedFileResponse>(
    `get_processed_file_data?file_id=${encodeURIComponent(fileId)}`
  );
  return response.data;
};
// ========== DataFeed Wizard API Functions ==========

// Session Management
const createWizardSession = async (
  fileId: string,
  uploadedFiles?: UploadedFileInfo[],
  name?: string,
  rowsType?: RowType,
  positionsFileId?: string
): Promise<SessionCreateResponse> => {
  const response = await axiosInstance.post(`datafeed/wizard/session`, {
    file_id: fileId,
    uploaded_files: uploadedFiles,
    name,
    rows_type: rowsType,
    positions_file_id: positionsFileId,
  });
  return response.data;
};

const checkWizardSessionNameAvailable = async (
  name: string,
  excludeSessionId?: string
): Promise<{ available: boolean }> => {
  const response = await axiosInstance.post<{ available: boolean }>(
    'datafeed/wizard/check-name',
    { name, exclude_session_id: excludeSessionId }
  );
  return response.data;
};

const updateWizardSessionName = async (
  sessionId: string,
  name: string
): Promise<void> => {
  await axiosInstance.put(`datafeed/wizard/session/${sessionId}/name`, {
    name,
  });
};

const getWizardSession = async (
  sessionId: string
): Promise<SessionDataResponse> => {
  const response = await axiosInstance.get<SessionDataResponse>(
    `datafeed/wizard/session/${sessionId}`
  );
  return response.data;
};

const deleteWizardSession = async (
  sessionId: string
): Promise<ServiceResponse> => {
  const response = await axiosInstance.delete<ServiceResponse>(
    `datafeed/wizard/session/${sessionId}`
  );
  return response.data;
};

// Step 2: Column Mapping
const getWizardFileColumns = async (
  sessionId: string
): Promise<FileColumnsResponse> => {
  const response = await axiosInstance.get(
    `datafeed/wizard/${sessionId}/columns`
  );
  return response.data;
};

const getWizardFileData = async (
  sessionId: string
): Promise<DataFeedFileResponse> => {
  const response = await axiosInstance.get<DataFeedFileResponse>(
    `datafeed/wizard/${sessionId}/file-data`
  );
  return response.data;
};

const updateProcessedCell = async (
  sessionId: string,
  rowIndex: number,
  columnName: string,
  newValue: string | number | boolean | null,
  fileId: string
): Promise<CellUpdateResponse> => {
  const response = await axiosInstance.put<CellUpdateResponse>(
    `datafeed/wizard/${sessionId}/update-processed-cell`,
    {
      row_index: rowIndex,
      column_name: columnName,
      new_value: newValue,
      file_id: fileId,
    }
  );
  return response.data;
};

const saveWizardColumnMapping = async (
  sessionId: string,
  config: ColumnMappingRequest
): Promise<SessionDataResponse> => {
  const response = await axiosInstance.put(
    `datafeed/wizard/${sessionId}/column-mapping`,
    config
  );
  return response.data;
};

// Step 3: Account Linking
const extractWizardAccounts = async (
  sessionId: string
): Promise<AccountsResponse> => {
  const response = await axiosInstance.post<AccountsResponse>(
    `datafeed/wizard/${sessionId}/extract-accounts`
  );
  return response.data;
};

const getWizardCustodians = async (): Promise<CustodiansResponse> => {
  const response = await axiosInstance.get<CustodiansResponse>(
    `datafeed/wizard/custodians`
  );
  return response.data;
};

const saveWizardAccountMappings = async (
  sessionId: string,
  mappings: AccountMappingRequest
): Promise<void> => {
  await axiosInstance.put(
    `datafeed/wizard/${sessionId}/account-mappings`,
    mappings
  );
};

const checkWizardAccountsStatus = async (
  sessionId: string
): Promise<AccountsMappingStatusResponse> => {
  const response = await axiosInstance.get<AccountsMappingStatusResponse>(
    `datafeed/wizard/${sessionId}/check-accounts-status`
  );
  return response.data;
};

// Step 4: Instrument Matching
const matchWizardInstruments = async (
  sessionId: string
): Promise<InstrumentsMatchResponse> => {
  const response = await axiosInstance.post<InstrumentsMatchResponse>(
    `datafeed/wizard/${sessionId}/match-instruments`
  );
  return response.data;
};

const saveWizardInstrumentMappings = async (
  sessionId: string,
  mappings: InstrumentMappingRequest['mappings']
): Promise<SessionDataResponse> => {
  const response = await axiosInstance.put<SessionDataResponse>(
    `datafeed/wizard/${sessionId}/instrument-mappings`,
    { mappings }
  );
  return response.data;
};

// Search instruments in Illio
const searchWizardInstruments = async (
  query: string,
  type?: string
): Promise<IllioSearchResponse> => {
  const response = await axiosInstance.get<IllioSearchResponse>(
    `datafeed/wizard/search-instruments`,
    { params: { query, ...(type && { type }) } }
  );
  return response.data;
};

// Fetch prices for instruments
const fetchWizardPrices = async (
  items: PriceFetchItem[]
): Promise<FetchPricesResponse> => {
  const response = await axiosInstance.post<FetchPricesResponse>(
    `datafeed/wizard/fetch-prices`,
    { items }
  );
  return response.data;
};

// Step 5: OTC Matching
const matchWizardOTC = async (sessionId: string): Promise<OTCMatchResponse> => {
  const response = await axiosInstance.post<OTCMatchResponse>(
    `datafeed/wizard/${sessionId}/match-otc`
  );
  return response.data;
};

const getWizardOTCInstruments = async (): Promise<OTCInstrumentsResponse> => {
  const response = await axiosInstance.get<OTCInstrumentsResponse>(
    `datafeed/wizard/otc-instruments`
  );
  return response.data;
};

const saveWizardOTCMappings = async (
  sessionId: string,
  mappings: OTCMappingRequest['mappings']
): Promise<SessionDataResponse> => {
  const response = await axiosInstance.put<SessionDataResponse>(
    `datafeed/wizard/${sessionId}/otc-mappings`,
    { mappings }
  );
  return response.data;
};

const moveWizardItem = async (
  sessionId: string,
  itemId: string,
  category: 'instrument' | 'otc'
): Promise<SessionDataResponse> => {
  const response = await axiosInstance.put<SessionDataResponse>(
    `datafeed/wizard/${sessionId}/items/${itemId}/move`,
    { category }
  );
  return response.data;
};

const advanceWizardStep = async (
  sessionId: string,
  targetStep: WizardStep
): Promise<void> => {
  await axiosInstance.put(`datafeed/wizard/${sessionId}/advance-step`, {
    target_step: targetStep,
  });
};

// Step 6: Finalize & Upload
const finalizeWizardUpload = async (
  sessionId: string
): Promise<FinalizeResponse> => {
  const response = await axiosInstance.post(
    `datafeed/wizard/${sessionId}/finalize`
  );
  return response.data;
};

const getWizardUploadStatus = async (
  sessionId: string
): Promise<UploadStatusResponse> => {
  const response = await axiosInstance.get<UploadStatusResponse>(
    `datafeed/wizard/${sessionId}/upload-status`
  );
  return response.data;
};

const getWizardStepStatus = async (
  sessionId: string
): Promise<WizardStepStatusResponse> => {
  const response = await axiosInstance.get<WizardStepStatusResponse>(
    `datafeed/wizard/${sessionId}/step-status`
  );
  return response.data;
};

const listWizardSessions = async (
  includeComplete: boolean = true
): Promise<WizardSessionsListResponse> => {
  const response = await axiosInstance.get(`datafeed/wizard/sessions`, {
    params: {
      include_complete: includeComplete,
    },
  });
  return response.data;
};

const compareWizardPositions = async (
  sessionId: string
): Promise<PositionComparisonResponse> => {
  const response = await axiosInstance.get<PositionComparisonResponse>(
    `datafeed/wizard/${sessionId}/positions/compare`
  );
  return response.data;
};

// =============================================================================
// Flows & Automations API - Templates
// =============================================================================

const getFlowsAutomationsTemplates = async (): Promise<TemplateListResponse> => {
  const response = await axiosInstance.get<TemplateListResponse>(
    `flows-automations/templates`
  );
  return response.data;
};

const createFlowsAutomationsTemplate = async (
  data: { name: string; template_type: string }
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/templates`,
    data
  );
  return response.data;
};

const updateFlowsAutomationsTemplate = async (
  templateId: string,
  data: Partial<{ name: string; template_type: string; field_mappings: Record<string, string> }>
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.put<FlowsActionResponse>(
    `flows-automations/templates/${templateId}`,
    data
  );
  return response.data;
};

const deleteFlowsAutomationsTemplate = async (
  templateId: string
): Promise<FlowsDeleteResponse> => {
  const response = await axiosInstance.delete<FlowsDeleteResponse>(
    `flows-automations/templates/${templateId}`
  );
  return response.data;
};

const uploadFlowsAutomationsTemplateFile = async (
  templateId: string,
  file: File
): Promise<FlowsActionResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/templates/${templateId}/upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

// PDF Editor APIs

const getTemplatePDFInfo = async (templateId: string): Promise<PDFInfoResponse> => {
  const response = await axiosInstance.get<PDFInfoResponse>(
    `flows-automations/templates/${templateId}/pdf-info`
  );
  return response.data;
};

const getTemplatePDFPageBlob = async (
  templateId: string,
  page: number,
  scale: number
): Promise<Blob> => {
  const response = await axiosInstance.get(
    `flows-automations/templates/${templateId}/pdf-page/${page}`,
    {
      params: { scale },
      responseType: 'blob',
    }
  );
  return response.data;
};

const saveTemplatePlaceholders = async (
  templateId: string,
  placeholders: PlaceholderData[]
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/templates/${templateId}/placeholders`,
    { placeholders }
  );
  return response.data;
};

const previewTemplatePDF = async (templateId: string): Promise<Blob> => {
  const response = await axiosInstance.get(
    `flows-automations/templates/${templateId}/preview`,
    { responseType: 'blob' }
  );
  return response.data;
};

const getDBColumns = async (): Promise<DBColumnsResponse> => {
  const response = await axiosInstance.get<DBColumnsResponse>(
    `flows-automations/db-columns`
  );
  return response.data;
};

const getDBColumnsFriendly = async (): Promise<DBColumnsFriendlyResponse> => {
  const response = await axiosInstance.get<DBColumnsFriendlyResponse>(
    `flows-automations/db-columns-friendly`
  );
  return response.data;
};

const getColumnValues = async (collection: string, field: string): Promise<ColumnValuesResponse> => {
  const response = await axiosInstance.get<ColumnValuesResponse>(
    `flows-automations/column-values/${collection}/${field}`
  );
  return response.data;
};

const generateFilledPDF = async (
  templateId: string,
  fieldValues: Record<string, string>,
  placeholders?: PlaceholderData[]
): Promise<Blob> => {
  const response = await axiosInstance.post(
    `flows-automations/templates/${templateId}/generate-filled`,
    { field_values: fieldValues, placeholders },
    { responseType: 'blob' }
  );
  return response.data;
};

const detectFontAtRect = async (
  templateId: string,
  page: number,
  rect: PlaceholderRect
): Promise<FontDetectionResponse> => {
  const response = await axiosInstance.post<FontDetectionResponse>(
    `flows-automations/templates/${templateId}/detect-font`,
    { page, rect }
  );
  return response.data;
};

// Client types for Generate Documents

const getFlowsAutomationsClients = async (): Promise<ClientListResponseTemplates> => {
  const response = await axiosInstance.get<ClientListResponseTemplates>(
    `flows-automations/clients`
  );
  return response.data;
};

const generateBulkDocuments = async (
  templateId: string,
  clientIds: string[]
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/generate-bulk`,
    { template_id: templateId, client_ids: clientIds }
  );
  return response.data;
};

const getAvailableCollections = async (): Promise<CollectionsListResponse> => {
  const response = await axiosInstance.get<CollectionsListResponse>(
    `flows-automations/collections`
  );
  return response.data;
};

const getClientRelatedData = async (
  clientId: string
): Promise<ClientRelatedDataResponse> => {
  const response = await axiosInstance.get<ClientRelatedDataResponse>(
    `flows-automations/clients/${clientId}/related-data`
  );
  return response.data;
};

// =============================================================================
// Flows & Automations - Generated Documents (saved per user in MongoDB)
// =============================================================================

const getSavedGeneratedDocuments = async (): Promise<GeneratedDocumentListResponse> => {
  const response = await axiosInstance.get<GeneratedDocumentListResponse>(
    `flows-automations/generated-documents`
  );
  return response.data;
};

const saveGeneratedDocument = async (
  data: {
    template_id: string;
    name: string;
    client_id?: string;
    client_name?: string;
    field_values: Record<string, unknown>;
    placeholders?: PlaceholderData[];
  }
): Promise<SaveGeneratedDocumentResponse> => {
  // Backend generates the PDF itself, so we just send JSON with the data
  const response = await axiosInstance.post<SaveGeneratedDocumentResponse>(
    `flows-automations/generated-documents`,
    {
      template_id: data.template_id,
      name: data.name,
      client_id: data.client_id,
      client_name: data.client_name,
      field_values: data.field_values,
      placeholders: data.placeholders,
    }
  );
  return response.data;
};

const downloadGeneratedDocument = async (documentId: string): Promise<Blob> => {
  const response = await axiosInstance.get(
    `flows-automations/generated-documents/${documentId}/download`,
    { responseType: 'blob' }
  );
  return response.data;
};

const deleteGeneratedDocument = async (documentId: string): Promise<FlowsDeleteResponse> => {
  const response = await axiosInstance.delete<FlowsDeleteResponse>(
    `flows-automations/generated-documents/${documentId}`
  );
  return response.data;
};

// =============================================================================
// Flows & Automations - Flows API
// =============================================================================

const getFlows = async (status?: string): Promise<FlowListResponse> => {
  const url = status ? `flows-automations/flows?status=${status}` : `flows-automations/flows`;
  const response = await axiosInstance.get<FlowListResponse>(url);
  return response.data;
};

const getFlow = async (flowId: string): Promise<FlowResponse> => {
  const response = await axiosInstance.get<FlowResponse>(`flows-automations/flows/${flowId}`);
  return response.data;
};

const createFlow = async (data: {
  name: string;
  description?: string;
  status?: string;
  conditions?: FlowConditionResponse[];
  conditions_logic?: string;
  email_action?: FlowEmailActionResponse;
  template_action?: FlowTemplateActionResponse;
}): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(`flows-automations/flows`, data);
  return response.data;
};

const updateFlow = async (
  flowId: string,
  data: {
    name?: string;
    description?: string;
    status?: string;
    conditions?: FlowConditionResponse[];
    conditions_logic?: string;
    email_action?: FlowEmailActionResponse | null;
    template_action?: FlowTemplateActionResponse | null;
  }
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.put<FlowsActionResponse>(`flows-automations/flows/${flowId}`, data);
  return response.data;
};

const deleteFlow = async (flowId: string): Promise<FlowsDeleteResponse> => {
  const response = await axiosInstance.delete<FlowsDeleteResponse>(
    `flows-automations/flows/${flowId}`
  );
  return response.data;
};

const executeFlow = async (
  flowId: string,
  data: { client_id?: string; trigger_data?: Record<string, unknown> }
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/flows/${flowId}/execute`,
    data
  );
  return response.data;
};

const testFlow = async (
  flowId: string,
  data: { trigger_data?: Record<string, unknown> }
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/flows/${flowId}/test`,
    data
  );
  return response.data;
};

const getFlowRuns = async (params?: {
  flow_id?: string;
  status?: string;
  limit?: number;
}): Promise<FlowRunListResponse> => {
  const response = await axiosInstance.get<FlowRunListResponse>(`flows-automations/flow-runs`, {
    params,
  });
  return response.data;
};

const recordFlowRun = async (data: RecordFlowRunRequest): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/flow-runs`,
    data
  );
  return response.data;
};

// =============================================================================
// Flows & Automations - Transactions API
// =============================================================================

interface CreateTransactionData {
  transaction_type: string;
  client_id: string;
  client_name: string;
  amount: number;
  currency: string;
  custodian_id?: string;
  custodian_name?: string;
  instrument_isin?: string;
  instrument_name?: string;
  side?: string;
  quantity?: number;
  price?: number;
  buy_currency?: string;
  sell_currency?: string;
  exchange_rate?: number;
  from_custodian_id?: string;
  from_custodian_name?: string;
  to_custodian_id?: string;
  to_custodian_name?: string;
  trade_date?: string;
  settlement_date?: string;
  notes?: string;
}

interface BulkTransactionItem {
  transaction_type: string;
  client_id: string;
  client_name: string;
  amount: number;
  currency: string;
  [key: string]: unknown;
}

const getTransactions = async (params?: {
  status?: string;
  transaction_type?: string;
  client_id?: string;
}): Promise<TransactionListResponse> => {
  const response = await axiosInstance.get<TransactionListResponse>(`flows-automations/transactions`, {
    params,
  });
  return response.data;
};

const createTransaction = async (data: CreateTransactionData): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/transactions`,
    data
  );
  return response.data;
};

const createBulkTransactions = async (
  transactions: BulkTransactionItem[]
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/transactions/bulk`,
    { transactions }
  );
  return response.data;
};

const updateTransaction = async (
  transactionId: string,
  data: Partial<TransactionResponse>
): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.put<FlowsActionResponse>(
    `flows-automations/transactions/${transactionId}`,
    data
  );
  return response.data;
};

const deleteTransaction = async (transactionId: string): Promise<FlowsDeleteResponse> => {
  const response = await axiosInstance.delete<FlowsDeleteResponse>(
    `flows-automations/transactions/${transactionId}`
  );
  return response.data;
};

const validateTransactions = async (): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/transactions/validate`
  );
  return response.data;
};

const submitTransactions = async (): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/transactions/submit`
  );
  return response.data;
};

// =============================================================================
// Flows & Automations - Email API
// =============================================================================

const sendTransactionEmail = async (
  data: SendTransactionEmailRequest,
  localFiles?: File[]
): Promise<SendTransactionEmailResponse> => {
  const formData = new FormData();
  formData.append("to", data.to);
  if (data.cc) formData.append("cc", data.cc);
  formData.append("subject", data.subject);
  formData.append("body", data.body);
  formData.append("transaction_ids", JSON.stringify(data.transaction_ids));
  if (data.document_ids) {
    formData.append("document_ids", JSON.stringify(data.document_ids));
  }

  if (localFiles) {
    localFiles.forEach((file) => {
      formData.append("attachments", file);
    });
  }

  const response = await axiosInstance.post<SendTransactionEmailResponse>(
    `flows-automations/transactions/send-email`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// =============================================================================
// Flows & Automations - Data Sources API
// =============================================================================

const getFlowClients = async (): Promise<FlowsClientListResponse> => {
  const response = await axiosInstance.get<FlowsClientListResponse>(`flows-automations/data/clients`);
  return response.data;
};

const getFlowCustodians = async (): Promise<CustodianListResponse> => {
  const response = await axiosInstance.get<CustodianListResponse>(`flows-automations/data/custodians`);
  return response.data;
};

const getFlowInstruments = async (): Promise<InstrumentListResponse> => {
  const response = await axiosInstance.get<InstrumentListResponse>(`flows-automations/data/instruments`);
  return response.data;
};

const getFlowFields = async (): Promise<FlowFieldsResponse> => {
  const response = await axiosInstance.get<FlowFieldsResponse>(`flows-automations/data/flow-fields`);
  return response.data;
};

const getEmailVariables = async (): Promise<EmailVariablesResponse> => {
  const response = await axiosInstance.get<EmailVariablesResponse>(`flows-automations/data/email-variables`);
  return response.data;
};

// Duplicate a flow
const duplicateFlow = async (flowId: string, name?: string): Promise<FlowsActionResponse> => {
  const response = await axiosInstance.post<FlowsActionResponse>(
    `flows-automations/flows/${flowId}/duplicate`,
    name ? { name } : {}
  );
  return response.data;
};

// Match flows against transaction data
const matchFlowsForTransaction = async (transactionData: Record<string, unknown>): Promise<FlowMatchTransactionResponse> => {
  const response = await axiosInstance.post<FlowMatchTransactionResponse>(
    `flows-automations/flows/match-transaction`,
    { transaction_data: transactionData }
  );
  return response.data;
};

// =============================================================================
// Gmail OAuth API
// =============================================================================

const getGmailOAuthStatus = async (): Promise<ServiceResponse> => {
  const response = await axiosInstance.get<ServiceResponse>(`gmail-oauth/status`);
  return response.data;
};

const getGmailAuthorizationUrl = async (): Promise<ServiceResponse> => {
  const response = await axiosInstance.get<ServiceResponse>(`gmail-oauth/authorize`);
  return response.data;
};

const revokeGmailOAuth = async (): Promise<ServiceResponse> => {
  const response = await axiosInstance.delete<ServiceResponse>(`gmail-oauth/revoke`);
  return response.data;
};

const testGmailConnection = async (): Promise<ServiceResponse> => {
  const response = await axiosInstance.post<ServiceResponse>(`gmail-oauth/test`);
  return response.data;
};

// =============================================================================
// Flows & Automations - Client Bank Accounts
// =============================================================================

const getClientBankAccounts = async (clientId: string): Promise<ServiceResponse> => {
  const response = await axiosInstance.get<ServiceResponse>(
    `flows-automations/data/clients/${clientId}/bank-accounts`
  );
  return response.data;
};

// Get generated documents for flows (for template action selection)
const getGeneratedDocumentsForFlows = async (): Promise<GeneratedDocumentListResponse> => {
  const response = await axiosInstance.get<GeneratedDocumentListResponse>(
    `flows-automations/data/generated-documents`
  );
  return response.data;
};

const completeWizardSession = async (sessionId: string): Promise<void> => {
  await axiosInstance.post(`datafeed/wizard/${sessionId}/complete`);
};

export default {
  // SSE Connection
  createSSEConnection,
  getAllFamiliesReport,
  generateReport,
  uploadDanelExcel,
  getOTCAssets,
  getPortfolioData,
  updateCustodianData,
  updateOTC,
  uploadOTCToIllio,
  syncOTCFromIllio,
  getUpdatedOTCComparedToIllio,
  checkAllOTCDifferences,
  checkOTCDifferencesRetry,
  deleteOTCColumn,
  downloadOTCBackup,
  restoreOTCFromBackup,
  getDanelData,
  uploadSingleDanelToIllio,
  uploadProcessedRows,
  getDanelExcelHeaders,
  saveDanelMappings,
  getDanelConfiguration,
  resetTableConfiguration,
  addSingleRowConfiguration,
  getSingleRowConfiguration,
  deleteSingleRowConfiguration,
  updateDanelFileCell,
  deleteDanelRow,
  getHoldings,
  getOTCDatabase,
  getOTCDatabaseWithLinks,
  syncOTCDatabaseFromIllio,
  updateOTCDatabaseRecord,
  updateHolding,
  syncPortfolioCustodianFromIllio,
  detectVaultTypes,
  parseVaultFiles,
  saveParsedVaults,
  processVaultFiles,
  // Views API - New unified structure
  getAllViews,
  getViewsForPage,
  saveViewsForPage,
  createView,
  updateView,
  deleteView,
  // Groups API
  getAllGroups,
  getGroupsForPage,
  saveGroupsForPage,
  // Authentication API functions
  login,
  logout,
  getCurrentUser,
  // Configuration Template API functions
  saveConfigurationTemplate,
  getConfigurationTemplate,
  listConfigurationTemplates,
  deleteConfigurationTemplate,
  renameConfigurationTemplate,
  applyTemplateRules,
  getProcessedFileData,
  // DataFeed Wizard
  createWizardSession,
  checkWizardSessionNameAvailable,
  updateWizardSessionName,
  getWizardSession,
  deleteWizardSession,
  listWizardSessions,
  getWizardFileColumns,
  getWizardFileData,
  saveWizardColumnMapping,
  extractWizardAccounts,
  getWizardCustodians,
  saveWizardAccountMappings,
  checkWizardAccountsStatus,
  matchWizardInstruments,
  saveWizardInstrumentMappings,
  searchWizardInstruments,
  fetchWizardPrices,
  matchWizardOTC,
  getWizardOTCInstruments,
  saveWizardOTCMappings,
  moveWizardItem,
  advanceWizardStep,
  finalizeWizardUpload,
  getWizardUploadStatus,
  getWizardStepStatus,
  compareWizardPositions,
  completeWizardSession,
  // Flows & Automations - Templates
  getFlowsAutomationsTemplates,
  createFlowsAutomationsTemplate,
  updateFlowsAutomationsTemplate,
  deleteFlowsAutomationsTemplate,
  uploadFlowsAutomationsTemplateFile,
  // Flows & Automations - PDF Editor
  getTemplatePDFInfo,
  getTemplatePDFPageBlob,
  saveTemplatePlaceholders,
  previewTemplatePDF,
  getDBColumns,
  getDBColumnsFriendly,
  getColumnValues,
  detectFontAtRect,
  generateFilledPDF,
  // Flows & Automations - Clients & Generate
  getFlowsAutomationsClients,
  generateBulkDocuments,
  // Flows & Automations - Collections & Related Data
  getAvailableCollections,
  getClientRelatedData,
  // Flows & Automations - Generated Documents (saved per user)
  getSavedGeneratedDocuments,
  saveGeneratedDocument,
  downloadGeneratedDocument,
  deleteGeneratedDocument,
  updateProcessedCell,
  // Flows & Automations - Flows
  getFlows,
  getFlow,
  createFlow,
  updateFlow,
  deleteFlow,
  executeFlow,
  testFlow,
  getFlowRuns,
  recordFlowRun,
  // Flows & Automations - Transactions
  getTransactions,
  createTransaction,
  createBulkTransactions,
  updateTransaction,
  deleteTransaction,
  validateTransactions,
  submitTransactions,
  sendTransactionEmail,
  // Flows & Automations - Data Sources
  getFlowClients,
  getFlowCustodians,
  getFlowInstruments,
  getFlowFields,
  getEmailVariables,
  // Flows & Automations - Additional Operations
  duplicateFlow,
  matchFlowsForTransaction,
  getGeneratedDocumentsForFlows,
  // Gmail OAuth
  getGmailOAuthStatus,
  getGmailAuthorizationUrl,
  revokeGmailOAuth,
  testGmailConnection,
  // Flows & Automations - Client Bank Accounts
  getClientBankAccounts,
};
